/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import uuidV4 from "uuid/v4";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import Events from "bundle/constants/Events";
import IFrameMessageTypes from "common/constants/IFrameMessageTypes";
import { providers } from "dc-core";

export const getLicenseTokenKey = clientId => `LTK__${clientId}`;
const getLicenseCheatInfoKey = clientId => `LTK__C__${clientId}`;
const API_ERROR_TIMEOUT = 30;
const API_FAILURE_MAX_RETRY = 3;
const CHEAT_CASE_MAX_DELAY_IN_DAYS = 30;
const MAX_NUMBER_EVENTS_IN_STORAGE = 50;

export default class LicensingService {
    initialize = () => {
        const appStore = ServiceRegistry.getService("AppStore");
        [Events.APP_RENDERING_DONE, Events.APP_RENDERING_FAILED].forEach(eventType => {
            ServiceRegistry.getService("EventsService").registerEventListener(
                eventType,
                () => {
                    if (appStore.isInPreview && !appStore.isIntegration) {
                        this.initializeHelper(appStore.configStore.getConfig());
                    }
                },
            );
        });
    };

    initializeHelper(config) {
        this._loggingService = ServiceRegistry.getService("LoggingService");
        this._messagingService = ServiceRegistry.getService("MessagingService");
        this._waitOnCallId = undefined;
        this._config = config;
        this._allOk = false;
        this._errorShown = false;
        this._timeoutCounter = 0;
        this._retryCount = 1;
        this._timeoutInterval = undefined;
        this._licenseCallResult = this._licenseCallResult.bind(this);
        this._checkForAllOk = this._checkForAllOk.bind(this);
        try {
            this.storage = typeof (Storage) !== "undefined" ? localStorage : undefined;
        } catch (e) {
            this.storage = undefined;
        }
        this._messagingService.listenForType(IFrameMessageTypes.LICENSE_CALL_RESULT, this._licenseCallResult);
        this._sendLicenseCall();
    }

    _log(...args) {
        // All warning logs from this service, so use wisely.
        const body = { ...this._getRequestBody() };
        delete body.events;
        this._loggingService.warn(
            "LicensingService",
            JSON.stringify(body),
            args,
        );
    }

    _checkForAllOk() {
        if (this._allOk || this._errorShown) {
            clearInterval(this._timeoutInterval);
            return;
        }

        this._timeoutCounter += 1;
        if (this._timeoutCounter < API_ERROR_TIMEOUT) {
            return;
        }

        if (this._retryCount <= API_FAILURE_MAX_RETRY) {
            this._requestForCall(5000);
            return;
        }

        this._waitOnCallId = undefined;

        clearInterval(this._timeoutInterval);
        // all in not OK due to what ever reason
        let cheatInfoString;
        if (this.storage) {
            cheatInfoString = this.storage.getItem(getLicenseCheatInfoKey(this._config.clientId));
        }
        let cheatInfo = {};
        const now = new Date().getTime();
        if (cheatInfoString) {
            try {
                cheatInfo = JSON.parse(cheatInfoString);
            } catch (e) {
                cheatInfo = {};
            }
        }

        if (!cheatInfo.start) {
            cheatInfo = {
                start: now,
            };
        }

        const limit = now - cheatInfo.start;
        if (limit >= CHEAT_CASE_MAX_DELAY_IN_DAYS * 86400 * 1000) {
            this._log("[Error] File preview blocked due to no response from server.", JSON.stringify(cheatInfo));
            this._gotInvalidLicense({ error: "CHEAT" });
        } else {
            this._log("No response from server, but not blocking UI", JSON.stringify(cheatInfo));
            if (this.storage) {
                this.storage.setItem(getLicenseCheatInfoKey(this._config.clientId), JSON.stringify(cheatInfo));
            }
        }
    }

    _sendLicenseCall() {
        // If SDK is running in local file then no need for check
        if (!this._config.eventOrigin || !this._config.licenseUri) {
            return;
        }
        const doServerCall = this._checkFromStorage();
        if (!doServerCall) {
            this.storeAnalyticsEventInStorage();
            return;
        }

        this._timeoutInterval = setInterval(this._checkForAllOk, 1000);
        this._requestForCall(100);
    }

    _requestForCall(delay) {
        if (this._retryCount > API_FAILURE_MAX_RETRY) {
            this._allOk = false;
            this._timeoutCounter = API_ERROR_TIMEOUT;
            // Intentionally not showing any blocking popup here..
            // As we don't want to block user due to our server error/cso
            this.storeAnalyticsEventInStorage(true);
            this._log("No response from service even after retries.");
            return;
        }

        this._retryCount += 1;
        this._timeoutCounter = 0;
        this._waitOnCallId = uuidV4();
        const data = {
            url: this._config.licenseUri,
            headers: {
                "X-Key-Pair-Version": "v1",
                "Content-Type": "application/json",
                "x-api-key": this._config.clientId,
            },
            body: this._getRequestBody(),
            callId: this._waitOnCallId,
        };

        setTimeout(() => this._messagingService.sendMessage(IFrameMessageTypes.LICENSE_CALL_INIT, data), delay);
    }

    _checkFromStorage() {
        let doServerCall = true;

        if (this.storage) {
            const licenseInfoString = this.storage.getItem(getLicenseTokenKey(this._config.clientId));
            if (licenseInfoString) {
                try {
                    const licenseInfo = JSON.parse(licenseInfoString);
                    const expiresInWithBuffer = new Date().getTime() - (3600 * 1000);
                    if (licenseInfo.expires_in && licenseInfo.expires_in > expiresInWithBuffer
                        && licenseInfo.origin === this._config.eventOrigin) {
                        doServerCall = false;
                    }
                    if (!doServerCall && licenseInfo.events) {
                        doServerCall = licenseInfo.events.length > MAX_NUMBER_EVENTS_IN_STORAGE;
                        if (doServerCall) {
                            this._log(`No. of events exceeds the limit of ${MAX_NUMBER_EVENTS_IN_STORAGE}`);
                        }
                    }
                } catch (e) {
                    this.storage.setItem(getLicenseTokenKey(this._config.clientId), "");
                    doServerCall = true;
                }
            }
        }

        return doServerCall;
    }

    _licenseCallResult(typeData) {
        if (typeData.callId !== this._waitOnCallId) {
            return;
        }

        const statusCode = typeData.statusCode;
        if (!statusCode) {
            this._log("Empty status is received (Probably due to CORS)", JSON.stringify(typeData));
            this._gotInvalidLicense({ error: "EMPTY_STATUS" });
            return;
        }

        const response = typeData.response;
        if (!response || statusCode > 499 || statusCode < 200) {
            this._log("Unexpected response is received:", JSON.stringify(typeData));
            // Try again for call
            this._requestForCall(5000);
            return;
        }

        if (statusCode !== 200) {
            this._log("Non 200 response is received", JSON.stringify(response));
            this._gotInvalidLicense({ error: response.reason });
            return;
        }

        const opts = {
            licenseString: response.access_token,
            domain: typeData.eventOrigin,
        };

        providers.rendition({}).then(renditionApi => {
            const licenseResponse = renditionApi.registerLicense(opts);
            licenseResponse.dcapi.catch(() => {});
            licenseResponse.ajs
                .then(result => {
                    this._gotValidLicense(result, response);
                })
                .catch(error => this._gotInvalidLicense(error));
        });
    }

    _gotValidLicense(result, licenseInfo) {
        const info = {
            expires_in: licenseInfo.expires_in + new Date().getTime(),
            origin: this._config.eventOrigin,
            events: [],
        };
        this._allOk = true;
        if (this.storage) {
            this.storage.setItem(getLicenseTokenKey(this._config.clientId), JSON.stringify(info));
            const cheatInfoString = this.storage.getItem(getLicenseCheatInfoKey(this._config.clientId));
            if (cheatInfoString) { // Clear cheat case and inform server
                this.storage.setItem(getLicenseCheatInfoKey(this._config.clientId), "");
                this._log("No response from server, cleared", cheatInfoString);
            }
        }
    }

    _getRequestBody() {
        let events = [];
        if (this.storage) {
            const licenseInfo = JSON.parse(this.storage.getItem(getLicenseTokenKey(this._config.clientId)));
            if (licenseInfo && licenseInfo.events) {
                events = licenseInfo.events || [];
            }
        }
        events.push(ServiceRegistry.getService("PerformanceService").getSessionEvent());
        return {
            client_id: this._config.clientId,
            client_device_time: new Date().getTime(),
            domain: this._config.eventOrigin,
            events,
        };
    }

    _gotInvalidLicense(error) {
        const dialogApi = ServiceRegistry.getService("DialogApi");
        if (error.error === "INVALID_CLIENT_LOCAL_TIME") {
            dialogApi.showBlockingDialog("dialog.license.title", "dialog.license.message.time");
        } else {
            /* Commenting this out until View SDK bundle fixes issue with license dialog */
            // dialogApi.showLicenseErrorPopup();
            ServiceRegistry.getService("AppStore").setLicenseErrorPopupOpen();
        }
        // Stop any retry and exit
        this._allOk = false;
        this._errorShown = true;
        this._retryCount = API_FAILURE_MAX_RETRY + 1;
        this._timeoutCounter = API_ERROR_TIMEOUT;
        this.storeAnalyticsEventInStorage(true);
        /* Commenting this out until View SDK bundle fixes issue with license dialog*/
        // ServiceRegistry.getService("AppStore").setValidLicense(false);
        this._log(`File preview is blocked for client ${JSON.stringify(error)}`);
    }

    storeAnalyticsEventInStorage(onlyEvents = false) {
        let events = [];
        if (this.storage) {
            const licenseInfo = JSON.parse(this.storage.getItem(getLicenseTokenKey(this._config.clientId)) || "{}");
            events = licenseInfo.events || [];
            events.push(ServiceRegistry.getService("PerformanceService").getSessionEvent());
            licenseInfo.events = events;
            this.storage.setItem(getLicenseTokenKey(this._config.clientId),
                JSON.stringify(onlyEvents ? { events } : licenseInfo));
        }
    }
}
